import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from './constants';
import { AGENDA_COLUMNS } from './agendaConfig';
import { agendaDateKey } from './agendaPresentation';
import { agendaMoveCoveringInterval, agendaMoveDateTime, agendaMoveFitsDay, agendaMoveOverlaps, type AgendaMoveTarget } from './agendaMove';
import { isSuccessfulAppointmentResponse, readAppointmentResponseBody } from './appointmentResponse';
import type { AppointmentFull } from './backendInterfaces';
import type { AgendaAppointmentInfoData } from './components/AgendaAppointmentInfo';

type MovingAppointment = { info: AgendaAppointmentInfoData; original: AppointmentFull; practitionerId: string };
type Availability = { status: 'loading' | 'ready' | 'error'; slots: number[]; dayAllowed: boolean };
type IntervalProof = { status: 'loading' | 'ready' | 'error'; allowed: boolean };
export type AgendaMoveCheck = { status: 'valid' | 'invalid' | 'loading'; message: string };
type Appointment = { id: string; when: string; duration: number; practitionerUserId: string };
const availabilityKey = (date: string, practitionerId: string) => `${date}|${practitionerId}`;
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const practitionerChangeMessage = 'Ein Behandlerwechsel wird von der vorhandenen Verschiebe-API nicht unterstützt. Bitte wählen Sie den bisherigen Behandler.';

export function useAgendaAppointmentMove({ visibleDates, appointments, loading, loadError, onMoved }: {
  visibleDates: readonly Date[];
  appointments: readonly Appointment[];
  loading: boolean;
  loadError: string;
  onMoved: () => void;
}) {
  const [moving, setMoving] = useState<MovingAppointment | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dragging, setDragging] = useState(false);
  const [hoverTarget, setHoverTarget] = useState<AgendaMoveTarget | null>(null);
  const [pendingTarget, setPendingTarget] = useState<AgendaMoveTarget | null>(null);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState<Record<string, Availability>>({});
  const [intervalProofs, setIntervalProofs] = useState<Record<string, IntervalProof>>({});
  const cache = useRef(new Map<string, Promise<Availability>>());
  const proofCache = useRef(new Map<string, Promise<IntervalProof>>());
  const dropRequest = useRef(0);
  const session = useRef(0);
  const savingRef = useRef(false);
  const preparingRef = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; session.current += 1; };
  }, []);

  const cancel = useCallback(() => {
    if (savingRef.current) return;
    session.current += 1;
    dropRequest.current += 1;
    setMoving(null); setHoverTarget(null); setPendingTarget(null); setDragging(false);
    setAvailability({}); cache.current.clear(); setError(''); setNotice('');
    setIntervalProofs({}); proofCache.current.clear();
  }, []);

  const begin = async (info: AgendaAppointmentInfoData) => {
    if (preparingRef.current || savingRef.current) return false;
    preparingRef.current = true; setPreparing(true); setError('');
    const activeSession = ++session.current;
    try {
      if (!localStorage.getItem('token')) throw new Error('Bitte melden Sie sich erneut an.');
      const response = await fetch(`${API_BASE_URL}/appointments/get-full-appointment/${encodeURIComponent(info.id)}`, { headers: headers() })
        .catch(() => { throw new Error('Die Verbindung zum Laden der Termindaten ist fehlgeschlagen. Bitte versuchen Sie es erneut.'); });
      if (!response.ok) throw new Error('Die vollständigen Termindaten konnten nicht geladen werden.');
      const original = await response.json() as AppointmentFull;
      if (!original || typeof original !== 'object' || original.id !== info.id || !Number.isInteger(original.duration) || original.duration <= 0
        || typeof original.when !== 'string' || Number.isNaN(new Date(original.when).getTime())
        || !Array.isArray(original.procedureIds) || !original.procedureIds.length || original.procedureIds.some((id) => typeof id !== 'string' || !id.trim())
        || !Array.isArray(original.patientIds) || !original.patientIds.length || original.patientIds.some((id) => typeof id !== 'string' || !id.trim())
        || !Array.isArray(original.practitionerIds) || original.practitionerIds.length !== 1
        || !AGENDA_COLUMNS.some((column) => column.practitionerId === original.practitionerIds[0])) {
        throw new Error('Dieser Termin kann mit den vorhandenen Daten nicht sicher per Drag-and-drop verschoben werden.');
      }
      if (!mounted.current || activeSession !== session.current) return false;
      cache.current.clear(); setAvailability({}); setNotice(''); setHoverTarget(null); setPendingTarget(null);
      dropRequest.current += 1; setIntervalProofs({}); proofCache.current.clear();
      setMoving({ original, practitionerId: original.practitionerIds[0], info: { ...info, when: original.when, duration: original.duration, description: original.description ?? info.description } });
      return true;
    } catch (cause) {
      if (mounted.current && activeSession === session.current) setError(cause instanceof Error ? cause.message : 'Der Verschiebemodus konnte nicht gestartet werden.');
      return false;
    } finally {
      preparingRef.current = false;
      if (mounted.current) setPreparing(false);
    }
  };

  const loadAvailability = (appointment: MovingAppointment, date: string, practitionerId: string, fresh = false): Promise<Availability> => {
    const key = availabilityKey(date, practitionerId);
    if (!fresh && cache.current.has(key)) return cache.current.get(key)!;
    const activeSession = session.current;
    if (mounted.current) setAvailability((current) => ({ ...current, [key]: { status: 'loading', slots: [], dayAllowed: false } }));
    const promise = (async (): Promise<Availability> => {
      try {
        const common = { patientId: appointment.original.patientIds[0], practitionerId,
          procedureId: appointment.original.procedureIds[0], duration: String(appointment.original.duration) };
        const [timesResponse, dayResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/appointments/possible`, { method: 'POST', headers: headers(), body: JSON.stringify({ ...common, date: `${date}T11:12:10` }) }),
          fetch(`${API_BASE_URL}/appointments/possible-date-infos`, { method: 'POST', headers: headers(), body: JSON.stringify({ ...common,
            startDate: `${date}T11:12:10`, endDate: `${date}T11:12:10`, appointmentId: appointment.original.id }) }),
        ]);
        if (!timesResponse.ok || !dayResponse.ok) throw new Error('Verfügbarkeit nicht verfügbar');
        const slots: unknown = await timesResponse.json(), days: unknown = await dayResponse.json();
        if (!Array.isArray(slots) || slots.some((slot) => typeof slot !== 'number' || !Number.isInteger(slot) || slot < 0 || slot >= 1440)
          || !Array.isArray(days) || days.length !== 1 || typeof days[0] !== 'string' || !days[0].trim()) throw new Error('Ungültige Verfügbarkeit');
        return { status: 'ready', slots, dayAllowed: days[0] === 'Y' };
      } catch { return { status: 'error', slots: [], dayAllowed: false }; }
    })().then((result) => {
      if (mounted.current && session.current === activeSession && cache.current.get(key) === promise) setAvailability((current) => ({ ...current, [key]: result }));
      return result;
    });
    cache.current.set(key, promise);
    return promise;
  };

  useEffect(() => {
    if (!moving) return;
    visibleDates.forEach((date) => { void loadAvailability(moving, agendaDateKey(date), moving.practitionerId); });
    // The cache belongs to the selected appointment; navigation only adds missing days.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moving, visibleDates]);

  const proofKey = (target: AgendaMoveTarget, interval: { anchor: number; duration: number }) =>
    `${availabilityKey(target.date, target.practitionerId)}|${interval.anchor}|${interval.duration}`;

  const loadIntervalProof = (appointment: MovingAppointment, target: AgendaMoveTarget, result: Availability, fresh = false): Promise<IntervalProof> => {
    const interval = agendaMoveCoveringInterval(result.slots, target.minute, appointment.original.duration);
    if (!interval) return Promise.resolve({ status: 'ready', allowed: false });
    const key = proofKey(target, interval);
    if (!fresh && proofCache.current.has(key)) return proofCache.current.get(key)!;
    const activeSession = session.current;
    setIntervalProofs((current) => ({ ...current, [key]: { status: 'loading', allowed: false } }));
    const promise = (async (): Promise<IntervalProof> => {
      try {
        const response = await fetch(`${API_BASE_URL}/appointments/possible`, { method: 'POST', headers: headers(), body: JSON.stringify({
          patientId: appointment.original.patientIds[0], practitionerId: target.practitionerId,
          procedureId: appointment.original.procedureIds[0], duration: String(interval.duration), date: `${target.date}T11:12:10`,
        }) });
        if (!response.ok) throw new Error('Zeitraumprüfung fehlgeschlagen');
        const starts: unknown = await response.json();
        if (!Array.isArray(starts) || starts.some((start) => typeof start !== 'number' || !Number.isInteger(start) || start < 0 || start >= 1440)) throw new Error('Ungültige Zeitraumantwort');
        return { status: 'ready', allowed: starts.includes(interval.anchor) };
      } catch { return { status: 'error', allowed: false }; }
    })().then((proof) => {
      if (mounted.current && session.current === activeSession && proofCache.current.get(key) === promise) setIntervalProofs((current) => ({ ...current, [key]: proof }));
      return proof;
    });
    proofCache.current.set(key, promise);
    return promise;
  };

  useEffect(() => {
    const candidate = pendingTarget || hoverTarget;
    if (!moving || !candidate || loading || loadError || !agendaMoveFitsDay(candidate, moving.original.duration)) return;
    const target = { ...candidate, practitionerId: candidate.practitionerId || (candidate.candidates.includes(moving.practitionerId) ? moving.practitionerId : '') };
    if (target.practitionerId !== moving.practitionerId) return;
    const userId = AGENDA_COLUMNS.find((column) => column.practitionerId === target.practitionerId)?.practitionerUserId;
    if (!userId || agendaMoveOverlaps(target, moving.original.duration, moving.original.id, userId, appointments)) return;
    const result = availability[availabilityKey(target.date, target.practitionerId)];
    if (!result || result.status !== 'ready' || !result.dayAllowed || result.slots.includes(target.minute)) return;
    const timer = window.setTimeout(() => { void loadIntervalProof(moving, target, result); }, 100);
    return () => window.clearTimeout(timer);
    // Stable per-interval cache deduplicates pointer updates without disabling any validation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moving, hoverTarget, pendingTarget, availability, loading, loadError, appointments]);

  const check = (target: AgendaMoveTarget, override?: Availability, proofOverride?: IntervalProof): AgendaMoveCheck => {
    if (!moving) return { status: 'invalid', message: 'Kein Termin ausgewählt.' };
    if (!agendaMoveFitsDay(target, moving.original.duration)) return { status: 'invalid', message: 'Die gesamte Termindauer muss zwischen 06:00 und 19:00 Uhr liegen.' };
    if (loading) return { status: 'loading', message: 'Termine werden geprüft…' };
    if (loadError) return { status: 'invalid', message: 'Die Agenda konnte nicht vollständig geladen werden.' };
    if (!target.practitionerId) {
      const possible = target.candidates.map((id) => check({ ...target, practitionerId: id }));
      const valid = possible.find((item) => item.status === 'valid');
      return valid ? { status: 'valid', message: 'Im Bestätigungsfenster den Behandler ausdrücklich auswählen.' }
        : possible.find((item) => item.status === 'loading') || possible[0] || { status: 'invalid', message: 'Bitte einen Behandlerfilter wählen.' };
    }
    if (!target.candidates.includes(target.practitionerId)) return { status: 'invalid', message: 'Bitte einen Behandler aus dem gewählten Filter auswählen.' };
    if (target.practitionerId !== moving.practitionerId) return { status: 'invalid', message: practitionerChangeMessage };
    const provider = AGENDA_COLUMNS.find((column) => column.practitionerId === target.practitionerId);
    if (!provider?.practitionerUserId) return { status: 'invalid', message: 'Dieser Bereich ist keinem Behandler zugeordnet.' };
    if (agendaMoveOverlaps(target, moving.original.duration, moving.original.id, provider.practitionerUserId, appointments)) return { status: 'invalid', message: 'Dieser Zeitraum ist bereits belegt.' };
    if (new Date(agendaMoveDateTime(target)).getTime() === new Date(moving.original.when).getTime()) return { status: 'invalid', message: 'Der Termin liegt bereits an dieser Stelle.' };
    const result = override || availability[availabilityKey(target.date, target.practitionerId)];
    if (!result || result.status === 'loading') return { status: 'loading', message: 'Verfügbarkeit wird geprüft…' };
    if (result.status === 'error') return { status: 'invalid', message: 'Die Verfügbarkeit konnte nicht zuverlässig geprüft werden. Bitte erneut versuchen.' };
    if (!result.dayAllowed) return { status: 'invalid', message: 'Dieser Tag ist nach den bestehenden Buchungsregeln nicht verfügbar.' };
    if (!result.slots.includes(target.minute)) {
      const interval = agendaMoveCoveringInterval(result.slots, target.minute, moving.original.duration);
      if (!interval) return { status: 'invalid', message: 'Für diesen Beginn liegt kein bestätigter freier Zeitraum vor.' };
      const proof = proofOverride || intervalProofs[proofKey(target, interval)];
      if (!proof || proof.status === 'loading') return { status: 'loading', message: 'Der vollständige Zielzeitraum wird geprüft…' };
      if (proof.status === 'error') return { status: 'invalid', message: 'Der Zielzeitraum konnte nicht zuverlässig geprüft werden. Bitte erneut versuchen.' };
      if (!proof.allowed) {
        const originalStart = new Date(moving.original.when).getTime();
        const targetStart = new Date(agendaMoveDateTime(target)).getTime();
        const overlapsOriginal = targetStart < originalStart + moving.original.duration * 60000 && targetStart + moving.original.duration * 60000 > originalStart;
        return { status: 'invalid', message: overlapsOriginal
          ? 'Die Backend-API bestätigt die Überlappung mit dem bisherigen Termin nicht. Ein sicherer Ausschluss des Alttermins wird dort nicht unterstützt.'
          : 'Das Backend bestätigt für diesen Beginn keinen freien Zeitraum für die vollständige Termindauer.' };
      }
    }
    return { status: 'valid', message: 'Verfügbarer Zeitraum.' };
  };

  const startDragging = () => { dropRequest.current += 1; setDragging(true); setNotice(''); };

  const drop = async (target: AgendaMoveTarget | null) => {
    const request = ++dropRequest.current, activeSession = session.current;
    setDragging(false); setHoverTarget(null);
    if (!target || !moving) return;
    let result = check(target);
    if (result.status === 'loading') {
      setNotice('Der vollständige Zielzeitraum wird geprüft…');
      const concrete = { ...target, practitionerId: target.practitionerId || moving.practitionerId };
      const latest = await loadAvailability(moving, target.date, concrete.practitionerId);
      if (!mounted.current || activeSession !== session.current || request !== dropRequest.current) return;
      const proof = latest.status === 'ready' && latest.dayAllowed && !latest.slots.includes(target.minute)
        ? await loadIntervalProof(moving, concrete, latest) : undefined;
      result = check(concrete, latest, proof);
    }
    if (!mounted.current || activeSession !== session.current || request !== dropRequest.current) return;
    if (result.status !== 'valid') { setNotice(result.message); return; }
    setError(''); setNotice(''); setPendingTarget(target);
  };

  const confirm = async () => {
    if (!moving || !pendingTarget || savingRef.current || !pendingTarget.practitionerId) return;
    const initialCheck = check(pendingTarget);
    if (initialCheck.status !== 'valid') { setError(initialCheck.message); return; }
    savingRef.current = true; setSaving(true); setError('');
    let backendConfirmed = false;
    try {
      if (!localStorage.getItem('token')) throw new Error('Bitte melden Sie sich erneut an.');
      const latest = await loadAvailability(moving, pendingTarget.date, pendingTarget.practitionerId, true);
      const proof = latest.status === 'ready' && latest.dayAllowed && !latest.slots.includes(pendingTarget.minute)
        ? await loadIntervalProof(moving, pendingTarget, latest, true) : undefined;
      const latestCheck = check(pendingTarget, latest, proof);
      if (latestCheck.status !== 'valid') throw new Error(latestCheck.message);
      const response = await fetch(`${API_BASE_URL}/appointments/move`, { method: 'POST', headers: headers(),
        body: JSON.stringify({ appointmentId: moving.original.id, when: agendaMoveDateTime(pendingTarget), mode: 'ergodent:demo' }) })
        .catch(() => { throw new Error('Die Verbindung zum Speichern der Verschiebung ist fehlgeschlagen. Bitte prüfen Sie die Agenda vor einem erneuten Versuch.'); });
      const body = await readAppointmentResponseBody(response)
        .catch(() => { throw new Error('Die Backend-Antwort konnte nicht gelesen werden. Bitte laden Sie die Agenda neu, bevor Sie es erneut versuchen.'); });
      if (!isSuccessfulAppointmentResponse(response, body)) throw new Error('Die Terminverschiebung wurde vom Backend nicht bestätigt. Bitte prüfen Sie die Verfügbarkeit und versuchen Sie es erneut.');
      backendConfirmed = true;
      if (mounted.current) {
        onMoved();
        session.current += 1; setMoving(null); setPendingTarget(null); setHoverTarget(null); setDragging(false);
        setAvailability({}); cache.current.clear(); setNotice('');
        setIntervalProofs({}); proofCache.current.clear();
      }
    } catch (cause) {
      if (mounted.current) setError(backendConfirmed ? 'Die Verschiebung wurde bestätigt, die Agenda konnte aber nicht aktualisiert werden. Bitte laden Sie die Ansicht neu.'
        : cause instanceof Error ? cause.message : 'Der Termin konnte nicht verschoben werden.');
    } finally { savingRef.current = false; if (mounted.current) setSaving(false); }
  };

  return { moving, preparing, error, notice, dragging, hoverTarget, pendingTarget, saving, begin, cancel, check, confirm, drop, startDragging,
    setDragging, setHoverTarget, setPendingTarget, setError, setNotice };
}
